import type { RouteParameters } from 'express-serve-static-core';

import type { JoinPaths } from './joinPaths';
import { joinPaths } from './joinPaths';
import type { CommonHttpMethod, RouteSchema } from './route-schema-types';
import type { StructCovariant } from './StructCovariant';

/**
 * Supertype for all valid HTTP request data for a given path.
 *
 * If the path contains parameters, then said parameters must be defined in the
 * `params` property.
 */
type AnyReqDataForPath<Path extends string> =
	(NonNullable<unknown> extends RouteParameters<Path>
		? { params?: never }
		: { params: Record<keyof RouteParameters<Path>, unknown> }) & {
		query?: Record<string, unknown>;
		headers?: Record<string, unknown>;
		body?: unknown;
	};

class RouteSchemaBuilder<Prefix extends `/${string}`> {
	constructor(public readonly pathPatternPrefix: Prefix) {}

	// TODO: Support defining params struct on a prefix.
	prefix<P extends string>(pathPatternPrefix: P) {
		return new RouteSchemaBuilder(
			joinPaths(this.pathPatternPrefix, pathPatternPrefix)
		);
	}

	private buildRoute<Method extends CommonHttpMethod>(method: Method) {
		return <
			Path extends string,
			S extends {
				req: StructCovariant<AnyReqDataForPath<JoinPaths<Prefix, Path>>>;
				res: StructCovariant;
			},
		>(
			pathPattern: Path,
			schema: S
		): RouteSchema<Method, JoinPaths<Prefix, Path>, S> => ({
			...schema,
			method,
			pathPattern: joinPaths(this.pathPatternPrefix, pathPattern),
		});
	}

	get = this.buildRoute('GET');
	post = this.buildRoute('POST');
	put = this.buildRoute('PUT');
	patch = this.buildRoute('PATCH');
	delete = this.buildRoute('DELETE');
}

export const adflowApiSchema = new RouteSchemaBuilder('/api');
